import { z } from "zod";
import { competenciaSchema } from "./competencia";
import { espalharNaCompetencia } from "./lancamento";

export const TIPOS_DE_BEM = ["MAQUINA", "INFORMATICA", "MOVEIS", "VEICULO", "OUTRO"] as const;
export type TipoDeBem = (typeof TIPOS_DE_BEM)[number];

/** Vida útil sugerida (a da tabela da Receita para cada tipo); o laboratório ajusta. */
export const TIPO_DE_BEM: Record<TipoDeBem, { rotulo: string; vidaUtilMeses: number }> = {
  MAQUINA: { rotulo: "Máquinas e equipamentos", vidaUtilMeses: 120 },
  INFORMATICA: { rotulo: "Computadores e informática", vidaUtilMeses: 60 },
  MOVEIS: { rotulo: "Móveis e instalações", vidaUtilMeses: 120 },
  VEICULO: { rotulo: "Veículos", vidaUtilMeses: 60 },
  OUTRO: { rotulo: "Outro", vidaUtilMeses: 60 },
};

export const bemSalvarSchema = z
  .object({
    id: z.string().min(1).optional(),
    nome: z.string().trim().min(2, "Dê um nome").max(80),
    tipo: z.enum(TIPOS_DE_BEM),
    valorCents: z.number().int().positive("Informe o valor"),
    /** Mês da compra (a depreciação começa nele). */
    compradoEm: competenciaSchema,
    vidaUtilMeses: z.number().int().min(1).max(600),
    valorResidualCents: z.number().int().min(0),
    lancamentoId: z.string().min(1).nullable().optional(),
  })
  .refine((b) => b.valorResidualCents < b.valorCents, { message: "O valor residual tem que ser menor que o valor do bem.", path: ["valorResidualCents"] });
export type BemSalvarInput = z.infer<typeof bemSalvarSchema>;

export const bemBaixarSchema = z.object({ id: z.string().min(1), baixadoEm: competenciaSchema.nullable() });

export type BemParaDepreciar = { valorCents: number; valorResidualCents: number; compradoEm: string; vidaUtilMeses: number; baixadoEm: string | null };

/** Depreciação linear do mês: (valor − residual) ÷ vida útil, do mês da compra até o fim da vida ou da baixa. */
export function depreciacaoNoMes(b: BemParaDepreciar, competencia: string): number {
  if (b.baixadoEm && competencia >= b.baixadoEm) return 0;
  return espalharNaCompetencia(b.valorCents - b.valorResidualCents, b.compradoEm, b.vidaUtilMeses).find((p) => p.competencia === competencia)?.valorCents ?? 0;
}

/** Depreciado até o mês (inclusive) e o que sobra de valor (contábil). */
export function situacaoDoBem(b: BemParaDepreciar, ate: string) {
  const fim = b.baixadoEm ?? "9999-12";
  const depreciadoCents = espalharNaCompetencia(b.valorCents - b.valorResidualCents, b.compradoEm, b.vidaUtilMeses)
    .filter((p) => p.competencia <= ate && p.competencia < fim)
    .reduce((t, p) => t + p.valorCents, 0);
  return { depreciadoCents, valorAtualCents: b.valorCents - depreciadoCents, mensalCents: Math.floor((b.valorCents - b.valorResidualCents) / b.vidaUtilMeses) };
}
